import React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import orderBy from "lodash/orderBy";
import { extractSlotFromLocationName } from "./utils";

const styles = {
  button: {
    py: 3,
    fontSize: 16,
    display: "flex",
    flex: 1,
  },
  grid: {
    display: "grid",
    gap: 2,
    width: "100%",
  },
  grid2: {
    gridTemplateColumns: "1fr 1fr",
  },
  grid3: {
    gridTemplateColumns: "1fr 1fr 1fr",
  },
};

/**
 * Maps slot values to their display order.
 * Case-insensitive matching ensures proper ordering.
 */
const SLOT_ORDER_MAP = {
  left: 1,
  center: 2,
  right: 3,
};

/**
 * Returns a numeric sort value for a location based on its slot.
 * @param {Object} loc - Location object
 * @returns {number} Sort order (1-3 for known slots, 999 for unknown)
 */
const getSlotSortOrder = (loc) => {
  if (!loc.slot || typeof loc.slot !== 'string') {
    return 999; // Unknown slots go to the end
  }
  
  const normalizedSlot = loc.slot.toLowerCase().trim();
  return SLOT_ORDER_MAP[normalizedSlot] || 999;
};

export default function Locations({ locations, selectedPosition, onPositionClick }) {
  // Sort by slot position: Left → Center → Right, with fallback to locationId
  const sortedLocations = orderBy(
    locations, 
    [getSlotSortOrder, "locationId"], 
    ["asc", "asc"]
  );
  
  const isAllSelected = selectedPosition.length === locations.length;
  const gridStyle = {
    ...styles.grid,
    ...(locations.length === 2 ? styles.grid2 : styles.grid3),
  };

  const handleSelectAll = () => {
    const allLocationNames = locations.map(loc => loc.locationName);
    onPositionClick(isAllSelected ? [] : allLocationNames, true);
  };

  const handlePositionClick = locationName => {
    onPositionClick([locationName]);
  };

  // Determine button label based on number of positions
  const selectAllLabel = locations.length === 2 ? "Both Positions" : "All Positions";
  
  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      {locations.length < 4 && (
        <Button
          fullWidth
          variant={isAllSelected ? "contained" : "outlined"}
          onClick={handleSelectAll}
        >
          {selectAllLabel}
        </Button>
      )}
      <Box sx={gridStyle}>
        {sortedLocations.map(loc => {
          // Extract display label from parentheses or fallback to slot
          const displayLabel = extractSlotFromLocationName({
            locationName: loc.locationName,
            slot: loc.slot,
          });
          
          return (
            <Button
              key={loc.locationId}
              variant={selectedPosition.includes(loc.locationName) ? "contained" : "outlined"}
              onClick={() => handlePositionClick(loc.locationName)}
              sx={styles.button}
            >
              {displayLabel}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}
